import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { TOAST_DEFAULT_DURATION } from './constants';
import { ToastContext } from './context';
import type { ToastItem, ToastOptions, ToastSeverity } from './types';

type ToastProviderProps = { children: ReactNode };

export default function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (
      title: string,
      severity: ToastSeverity,
      options: ToastOptions = {},
    ): string => {
      const id = crypto.randomUUID();
      const duration = options.duration ?? TOAST_DEFAULT_DURATION[severity];
      setToasts((prev) => [
        ...prev,
        {
          id,
          title,
          description: options.description,
          severity,
          duration,
          action: options.action,
          dismissOnClick: options.dismissOnClick,
          onClick: options.onClick,
          icon: options.icon,
        },
      ]);
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  const contextValue = useMemo(
    () => ({ toasts, show, dismiss }),
    [toasts, show, dismiss],
  );

  return <ToastContext value={contextValue}>{children}</ToastContext>;
}
